import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CabinCategoryCfmModel,
  ItineraryResponse,
  OBSObjI,
  OnboardServicesByCruiseIdMyMSCResponseI,
  SelectedDayFilter,
} from '@msc-b2c/interfaces/types';
import {
  AllBooking,
  CheckInsMyMSCResponse,
  FinalizePaymentMyMscResponse,
  GetBookingMyMscResponse,
  GetExcursionsMyMSCResponse,
  GetShopExcursionMyMSCResponse,
  MyMscExcursion,
  OBSI,
  PriceToBookCruiseMyMscPaymentOptionsI,
  RetrieveVoyagerClubInfoMyMSCResponse,
  SlotsI,
} from '@msc-b2c/interfaces/interfaces';
import { calculateExcDayByCruiseDepartureDate } from '../../utilFuncs/utilFuncs';
import { loadFromSessionStorage } from '../../genericUtils/genericUtils';

export interface MyCruiseState {
  itinerary?: ItineraryResponse | null;
  excursions?: GetExcursionsMyMSCResponse | null;
  obs?: OnboardServicesByCruiseIdMyMSCResponseI | null;
  slots?: SlotsI | null;
  selectedDayFilter?: SelectedDayFilter | null;
  booking?: GetBookingMyMscResponse | null; //TODO: REMOVE THIS
  shopExcursion: GetShopExcursionMyMSCResponse | null;
  shopOBS: OBSI | null;
  checkInList: CheckInsMyMSCResponse | null;
  cabinDetails: CabinCategoryCfmModel | null;
  allBookings?: AllBooking[] | null;
  bookingPayment: PriceToBookCruiseMyMscPaymentOptionsI[] | null;
  finalizeBookingPayment: FinalizePaymentMyMscResponse | null;
  availableServiceTypesInPage: string[];
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
  membershipDataIsLoading: boolean;
  activeBookingNumber?: string | null;
}

const initialState: MyCruiseState = {
  itinerary: null,
  excursions: null,
  obs: null,
  slots: null,
  selectedDayFilter: {
    portCode: '',
    date: 'allPorts',
    isEmbarkationDay: false,
    isDisembarkationDay: false,
  },
  booking: null, //TODO: REMOVE THIS
  shopExcursion: null,
  shopOBS: null,
  checkInList: null,
  cabinDetails: null,
  allBookings: null,
  bookingPayment: null,
  finalizeBookingPayment: null,
  availableServiceTypesInPage: [],
  membershipData: null,
  membershipDataIsLoading: false,
  activeBookingNumber: null,
};

const myCruiseSlice = createSlice({
  name: 'myCruiseData',
  initialState: loadFromSessionStorage('myCruiseData', initialState),
  reducers: {
    // reducers to be used for logic in components
    daySelected(state, action: PayloadAction<SelectedDayFilter>) {
      state.selectedDayFilter = action.payload;
      //check if the selectedDay is embarkation or disembarkation day
      //embarkation day is the first date in the itinerary, disembarkation day is the last date in the itinerary
      if (state.itinerary) {
        const embarkationDate = state.itinerary.days[0].date;
        const disembarkationDate = state.itinerary.days[state.itinerary.days.length - 1].date;
        state.selectedDayFilter.isEmbarkationDay = action.payload.date === embarkationDate;
        state.selectedDayFilter.isDisembarkationDay = action.payload.date === disembarkationDate;
      }
    },
    // reducers used for data retrieval from API
    myAreaItinerary(state, action: PayloadAction<ItineraryResponse>) {
      state.itinerary = action.payload;
      if (state.itinerary)
        state.itinerary.selectedDayFilter = {
          portCode: '',
          date: 'allPorts',
        };
    },
    myAreaExcursions(state, action: PayloadAction<GetExcursionsMyMSCResponse>) {
      state.excursions = action.payload;
      if (state.excursions?.excursions?.length > 0 && state.itinerary)
        state.excursions.excursions.forEach((excursion: MyMscExcursion) => {
          excursion.day = calculateExcDayByCruiseDepartureDate(
            excursion.startDate,
            state.itinerary?.departureDate
          );
        });
      // set index incrementally for each excursion that will be listed to the user
      // useful for analytics index
      let index = 1;
      const itineraryLastDay = state.itinerary?.cruiseDuration
        ? state.itinerary?.cruiseDuration + 1
        : null;
      state.excursions?.excursions?.forEach((excursion: MyMscExcursion) => {
        if (
          (Number(excursion?.inventory?.numberAvailable) > 0 &&
            excursion.day !== 1 &&
            excursion.day !== itineraryLastDay) ||
          (excursion.day == 1 && (excursion.isPackage || excursion.code.endsWith('E'))) ||
          (excursion.day == itineraryLastDay && excursion.code.endsWith('D'))
        )
          excursion.index = index++;
      });
    },
    myAreaOBS(state, action: PayloadAction<OnboardServicesByCruiseIdMyMSCResponseI>) {
      state.obs = action.payload;

      // set index incrementally for each obs that will be listed to the user
      // useful for analytics index
      let index = 1;
      state.obs?.onboardServices?.forEach((obs: OBSObjI) => {
        obs.index = index++;
      });
    },
    myAreaSlots(state, action: PayloadAction<SlotsI>) {
      state.slots = action.payload;
    },
    myAreaShopExcursion(state, action: PayloadAction<GetShopExcursionMyMSCResponse>) {
      state.shopExcursion = action.payload;
    },
    myAreaShopOBS(state, action: PayloadAction<OBSI>) {
      state.shopOBS = action.payload;
    },
    myAreaAvailableServiceTypeInPage(state, action: PayloadAction<string[]>) {
      const incoming = action.payload;
      // Add serviceType only if it is not already present in the state
      const merged = new Set([...state.availableServiceTypesInPage, ...incoming]);
      state.availableServiceTypesInPage = Array.from(merged);
    },
    myAreaAvailableServiceTypeInPageReset(state) {
      state.availableServiceTypesInPage = [];
    },
    myAreaCheckIn(state, action: PayloadAction<CheckInsMyMSCResponse>) {
      state.checkInList = action.payload;
    },
    myAreaCabinDetails(state, action: PayloadAction<CabinCategoryCfmModel>) {
      state.cabinDetails = action.payload;
    },
    myAreaAllBookings(state, action: PayloadAction<AllBooking[]>) {
      state.allBookings = action.payload;
    },
    myAreaBookingPayment(
      state,
      action: PayloadAction<PriceToBookCruiseMyMscPaymentOptionsI[] | null>
    ) {
      state.bookingPayment = action.payload;
    },
    myAreaFinalizeBookingPayment(state, action: PayloadAction<FinalizePaymentMyMscResponse>) {
      state.finalizeBookingPayment = action.payload;
    },
    membershipsData(
      state,
      action: PayloadAction<RetrieveVoyagerClubInfoMyMSCResponse | undefined>
    ) {
      state.membershipData = action.payload;
    },
    membershipDataIsLoading(state, action: PayloadAction<boolean>) {
      state.membershipDataIsLoading = action.payload;
    },
    setActiveBookingNumber(state, action: PayloadAction<string | null>) {
      state.activeBookingNumber = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder.addCase('resetMyCruiseData', (state) => {
      state = initialState;
      return state;
    }),
});

export const {
  daySelected,
  myAreaItinerary,
  myAreaExcursions,
  myAreaOBS,
  myAreaSlots,
  myAreaShopExcursion,
  myAreaCheckIn,
  myAreaCabinDetails,
  myAreaAllBookings,
  myAreaBookingPayment,
  myAreaFinalizeBookingPayment,
  myAreaShopOBS,
  myAreaAvailableServiceTypeInPage,
  myAreaAvailableServiceTypeInPageReset,
  membershipsData,
  membershipDataIsLoading,
  setActiveBookingNumber,
} = myCruiseSlice.actions;
export default myCruiseSlice.reducer;
